/**
 * Passive console capture ring buffer.
 *
 * Intercepts console.error and console.warn, preserving original behavior,
 * and stores the last N entries in memory. Entries are only read when a
 * bug report is submitted — zero cost during normal operation.
 */

interface ConsoleEntry {
  timestamp: string;
  level: "error" | "warn";
  args: string[];
}

const MAX_ENTRIES = 30;
const MAX_ARG_LENGTH = 1000;

let entries: ConsoleEntry[] = [];
let installed = false;
let originalError: typeof console.error | null = null;
let originalWarn: typeof console.warn | null = null;

function serializeArg(arg: unknown): string {
  try {
    if (arg instanceof Error) {
      return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack.slice(0, 500)}` : ""}`;
    }
    if (typeof arg === "function") return "[Function]";
    if (typeof arg === "undefined") return "undefined";
    if (arg === null) return "null";
    if (typeof arg === "string") return arg.slice(0, MAX_ARG_LENGTH);
    if (typeof arg === "number" || typeof arg === "boolean") return String(arg);
    const json = JSON.stringify(arg, null, 0);
    return json.length > MAX_ARG_LENGTH ? json.slice(0, MAX_ARG_LENGTH) + "…" : json;
  } catch {
    return "[Unserializable]";
  }
}

function pushEntry(level: "error" | "warn", args: unknown[]): void {
  entries.push({
    timestamp: new Date().toISOString(),
    level,
    args: args.map(serializeArg),
  });
  if (entries.length > MAX_ENTRIES) entries.shift();
}

export const consoleCapture = {
  install(): void {
    if (installed) return;
    installed = true;
    originalError = console.error;
    originalWarn = console.warn;

    console.error = function (...args: unknown[]) {
      pushEntry("error", args);
      if (originalError) originalError.apply(console, args);
    };

    console.warn = function (...args: unknown[]) {
      pushEntry("warn", args);
      if (originalWarn) originalWarn.apply(console, args);
    };
  },

  uninstall(): void {
    if (!installed) return;
    if (originalError) console.error = originalError;
    if (originalWarn) console.warn = originalWarn;
    originalError = null;
    originalWarn = null;
    installed = false;
  },

  getEntries(): ConsoleEntry[] {
    return [...entries];
  },

  clear(): void {
    entries = [];
  },
};
